// ── THE FARMER'S FIELD MAPPER HOME ───────────────────────────────────────────
//
// Kevin, 2026-10-31, on what a farmer must be able to do easily here: see their fields,
// see fields and acres by crop, see yield maps, see planting maps, and see totals for
// planting and yield by crop and type (grain vs silage) — plus "export a PDF of yield
// summaries by operation totals (all of a farmers wheat crop, or corn, etc)". He approved
// a four-screen phone mockup the same day: this home, one crop's fields, a field's maps
// (that is the Machine data Maps tab, deep-linked — not rebuilt), and the season totals.
//
// It is the landing view of Field Mapper in the portal; the map and every other page stay
// one tap away and come back here. Agents open the same page for a selected farm from the
// "Season" button, so they see exactly what the farmer sees.
//
// ⚠ NO NEW PIPELINE, AND NO SECOND SET OF RULES. The numbers come from ./seasonTotals,
// which reads the same ?fn=jd-fielddata rows, the same calibration and the same
// grain-vs-silage, unit and no-cross-crop-average rules as Machine data; the reported
// acres are the policy_fields rows Field Mapper already loaded. Every read here carries
// ?farmer=, because a preview-as session holds an ADMIN token and an unscoped read would
// answer with the whole book. authedFetch is passed IN, as YieldImportPanel's is: this
// renders in the portal, and a component that resolves its own session there is the
// shape check-home-safe forbids.
//
// The App.jsx helpers it needs (FmPageHeader, fmCropName, jdYieldUnitLabel, jdIsSilage,
// jdCropUninsured, fieldCropColor) come in on `ui`, so there is one definition of each.
import React, { useEffect, useMemo, useState } from "react";
import { buildSeason, cropFields, fieldsByLine, avgUnitText, prodUnitText, fmtAvg, fmtAc, fmtProd, fmtRate, mdText, STATE_TEXT } from "./seasonTotals";
import { buildSeasonPdf } from "./seasonPdf";

const INK = "#1a2e1a", ACCENT = "#a8d878", BORDER = "#cdd9cd", SOFT = "#4f6a4c", MUTED = "#60735f";
const SERIF = "'Lora','Georgia',serif";

// One fetch per farm (and per farm-year for the seed-rate grids), kept for a few minutes,
// so going to the map and back, or changing year, does not ask the server again.
const CACHE = new Map();
const TTL_MS = 5 * 60 * 1000;
function cached(key, load) {
  const hit = CACHE.get(key);
  if (hit && Date.now() - hit.t < TTL_MS) return hit.p;
  const p = load().catch((e) => { CACHE.delete(key); throw e; });
  CACHE.set(key, { t: Date.now(), p });
  return p;
}

const card = { background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 16, padding: "12px 14px", marginTop: 10 };
const lbl = { fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", color: SOFT, textTransform: "uppercase" };
const num = { fontVariantNumeric: "tabular-nums" };
const acRound = (v) => (v == null ? "—" : Math.round(v).toLocaleString("en-US"));
const plural = (n, w) => `${n.toLocaleString("en-US")} ${w}${n === 1 ? "" : "s"}`;
// How the corn was harvested, in the few words a row has room for.
const SPLIT_SHORT = { combined: "combined", chopped: "chopped", appraised: "appraised", unclear: "harvested", map_pending: "harvested", not_yet: "no harvest on file", pp: "prevent plant" };
const splitShort = (split) => split.map((x) => `${acRound(x.ac)} ${SPLIT_SHORT[x.key] || x.label}`).join(" · ");
const SPLIT_COLOR = { not_yet: "#dfe6dc", pp: "#c9c2b0", unclear: "#d9a441", map_pending: "#e8c77a", appraised: "#b58f3a" };
// An insured field a grower knows by its FSA farm and field number.
const farmField = (fsn, field) => `Farm ${fsn || "not given"}${field ? ` · Field ${field}` : ""}`;

function Bar({ pct, color }) {
  return (
    <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", marginTop: 10, background: "#e3eae0" }}>
      <div style={{ width: `${Math.max(0, Math.min(100, pct || 0))}%`, background: color || "#54a24a" }} />
    </div>
  );
}
function Swatch({ color, size = 12 }) {
  return <span aria-hidden="true" style={{ width: size, height: size, borderRadius: 3, background: color, display: "inline-block", flexShrink: 0 }} />;
}
const YieldIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="3" /><path d="M3 15l5-5 4 4 3-3 6 6" />
  </svg>
);
const PlantIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M12 21V11" /><path d="M12 11c0-4 3-6 7-6 0 4-3 6-7 6z" /><path d="M12 14c0-3-2-5-6-5 0 3 2 5 6 5z" />
  </svg>
);

export default function FarmerSeasonHome({
  farm = null, farmPicker = null, isPortal = true, isMobile = false,
  year, years = [], onYear = null, policyRows = null, policyYear = null, hasMachine = null,
  authedFetch, page: pageProp, onPage: onPageProp, onBack = null, onOpenMap, onOpenMachine, ui = {},
}) {
  const { PageHeader, cropName = (c) => String(c || ""), unitLabel = (c, u) => u || "unit not recorded",
          isSilage = (c) => /SILAGE/i.test(String(c || "")), isCover = () => false, cropColor = () => "#9aa89a" } = ui;
  const [pageLocal, setPageLocal] = useState(null);
  const page = pageProp !== undefined ? pageProp : pageLocal;
  const setPage = onPageProp || setPageLocal;
  const farmId = farm && farm.id;

  // ── the machine data (same payload Machine data loads) ──
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setData(null); setErr(null);
    if (!farmId || !hasMachine || !authedFetch) return;
    let alive = true;
    cached(`fd|${farmId}`, () => authedFetch(`/api/market?fn=jd-fielddata&farmer=${encodeURIComponent(farmId)}`)
      .then((r) => r.json()).then((d) => { if (!d || d.error) throw new Error((d && d.error) || "no data"); return d; }))
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(String((e && e.message) || e)); });
    return () => { alive = false; };
  }, [farmId, hasMachine]); // eslint-disable-line
  // Seed-rate grids: the planting RATE and its unit. Wheat drilled last fall is this crop
  // year's, so the year before is read too and seasonTotals keeps only this crop year.
  const [grids, setGrids] = useState(null);
  useEffect(() => {
    setGrids(null);
    if (!farmId || !hasMachine || !authedFetch || !year) return;
    let alive = true;
    const one = (y) => cached(`sg|${farmId}|${y}`, () => authedFetch(`/api/market?fn=jd-opsgrids&farmer=${encodeURIComponent(farmId)}&metric=seed_rate&novalues=1&year=${y}`)
      .then((r) => r.json()).then((d) => ((d && d.grids) || []).map((g) => ({ ...g, metric: "seed_rate" }))));
    Promise.all([one(year), one(Number(year) - 1)])
      .then(([a, b]) => { if (alive) setGrids([...a, ...b]); })
      .catch(() => { if (alive) setGrids([]); });
    return () => { alive = false; };
  }, [farmId, hasMachine, year]); // eslint-disable-line

  // HOW EACH INSURED CORN FIELD WAS HARVESTED, AND ITS APPRAISAL (api/_season-corn.js). Read
  // whether or not the farm has John Deere: a farm with appraisals and no machine data still
  // has its appraisals to see. A failed read leaves the corn row on its machine acres.
  const [corn, setCorn] = useState(null);
  useEffect(() => {
    setCorn(null);
    if (!farmId || !authedFetch || !year) return;
    let alive = true;
    cached(`sc|${farmId}|${year}`, () => authedFetch(`/api/market?fn=jd-season-corn&farmer=${encodeURIComponent(farmId)}&year=${year}`)
      .then((r) => r.json()).then((d) => { if (!d || d.error || !d.ok) throw new Error((d && d.error) || "no data"); return d.corn || null; }))
      .then((c) => { if (alive) setCorn(c); })
      .catch(() => { if (alive) setCorn(null); });
    return () => { alive = false; };
  }, [farmId, year]); // eslint-disable-line

  const h = useMemo(() => ({ cropName, unitLabel, isSilage, isCover }), [cropName, unitLabel, isSilage, isCover]);
  const rowsOk = policyRows && (policyYear == null || String(policyYear) === String(year));
  const season = useMemo(() => buildSeason({
    byField: (data && data.machine && data.machine.byField) || [], calibration: data && data.calibration,
    policyRows: rowsOk ? policyRows : null, seedGrids: grids, year, farmId, corn,
  }, h), [data, grids, policyRows, rowsOk, year, farmId, h, corn]);
  // A John Deere crop's colour: CORN SILAGE is corn type 026, CORN is grain. A reported
  // line keeps its own type code, so corn with no type reported takes the map's own
  // "type not reported" colour rather than grain's.
  const colorOf = (crop) => (crop === "CORN SILAGE" ? cropColor("CORN", "026") : crop === "CORN" ? cropColor("CORN", "016") : cropColor(crop));
  const rowColor = (r) => (r.fsaAc != null ? cropColor(r.crop, r.tc) : colorOf(r.crop));
  const loadingMachine = hasMachine && !data && !err;

  // ── header ──
  const yearSelect = (years || []).length > 1 && onYear ? (
    <select value={year ?? ""} onChange={(e) => onYear(Number(e.target.value))} aria-label="Crop year"
      style={{ height: 40, padding: "0 14px", borderRadius: 20, border: "none", fontFamily: "inherit", fontSize: 16, fontWeight: 800,
               background: INK, color: ACCENT, cursor: "pointer" }}>
      {years.map((y) => <option key={y} value={y}>{y}</option>)}
    </select>
  ) : null;
  const header = (title, sub, back) => (PageHeader && back) ? (
    <PageHeader backLabel="Field Mapper" onBack={back} title={title} sub={sub}>
      {yearSelect}{farmPicker}
    </PageHeader>
  ) : (
    <div style={{ padding: "4px 2px 0" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: SERIF, fontSize: 22, fontWeight: 700, lineHeight: 1.15, color: INK }}>{title}</div>
          {sub && <div style={{ fontSize: 12.5, color: SOFT, marginTop: 2 }}>{sub}</div>}
        </div>
        {yearSelect}
      </div>
      {farmPicker && <div style={{ marginTop: 10 }}>{farmPicker}</div>}
    </div>
  );
  const wrap = (children) => (
    <div style={{ maxWidth: isMobile ? "none" : 640, margin: isMobile ? 0 : "0 auto", paddingBottom: 24, fontFamily: "inherit", color: INK }}>{children}</div>
  );

  if (!farm) {
    return wrap(<>
      {header("Season", null, onBack)}
      <div style={card}>
        <div style={{ fontSize: 14, color: SOFT, lineHeight: 1.5 }}>Pick a farm to see its season: fields and acres by crop, yield and planting maps, and totals.</div>
      </div>
    </>);
  }

  const openMaps = (metric, extra = {}) => onOpenMachine && onOpenMachine({ view: "maps", metric, year, backLabel: "Field Mapper", ...extra });
  const machineLine = !hasMachine ? (hasMachine === false
    ? (isPortal ? "Yield and planting maps show here once your John Deere account is connected." : "This farm isn't connected to John Deere yet.")
    : "Checking for machine data…") : err ? "Your machine data didn't load. Check your connection and open this page again." : null;

  // ════════════════════════════════════════════════════════════════════════
  // ONE CROP'S FIELDS
  // ════════════════════════════════════════════════════════════════════════
  if (page && page.kind === "crop") {
    const row = season.cropRows.find((r) => r.key === page.key);
    // A crop from another year (the year was changed while this page was open) or one the
    // machine data has not delivered yet: say so, with the way back, rather than a blank.
    if (!row) return wrap(<>
      {header(`${year} season`, farm.name, () => setPage(null))}
      <div style={{ ...card, fontSize: 13, color: SOFT }}>{loadingMachine ? "Loading your machine data…" : `That crop isn't in your ${year} season.`}</div>
    </>);
    return <CropPage row={row} season={season} data={data} year={year} farm={farm} header={header} wrap={wrap}
      onBack={() => setPage(null)} onPick={(k) => setPage({ kind: "crop", key: k })} color={rowColor(row)} h={h}
      loadingMachine={loadingMachine} machineLine={machineLine}
      onField={(r) => onOpenMachine && r.jd_field_id && onOpenMachine({ view: "maps", jdFieldId: r.jd_field_id, fieldName: r.field, crop: r.crop,
        year, metric: r.harv_ac != null ? "yield" : "seed_rate", backLabel: row.label })} />;
  }

  // ════════════════════════════════════════════════════════════════════════
  // SEASON TOTALS
  // ════════════════════════════════════════════════════════════════════════
  if (page && page.kind === "totals") {
    return <TotalsPage season={season} data={data} year={year} farm={farm} header={header} wrap={wrap} onBack={() => setPage(null)}
      colorOf={colorOf} h={h} loadingMachine={loadingMachine} machineLine={machineLine} gridsLoading={hasMachine && grids == null} />;
  }

  // ════════════════════════════════════════════════════════════════════════
  // HOME
  // ════════════════════════════════════════════════════════════════════════
  const fsa = season.fsa && season.fsa.lines.length ? season.fsa : null;
  const prog = season.progress;
  const plantedAc = season.planting.reduce((s, l) => s + l.ac, 0);
  return wrap(<>
    {header(`${isPortal ? "Your " : ""}${year} season`, isPortal
      ? `${farm.name ? farm.name + " · " : ""}from your reported acres and your John Deere machines`
      : `${farm.name || "Farm"} · what the farmer sees`, onBack)}

    {/* MY FIELDS */}
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <span style={lbl}>{isPortal ? "My fields" : "Fields"}</span>
        <button onClick={onOpenMap} style={{ border: "none", background: "none", padding: "8px 0", minHeight: 36, fontFamily: "inherit", fontSize: 13, fontWeight: 700, color: "#2d6a2d", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>
          See all on the map
        </button>
      </div>
      <div style={{ display: "flex", gap: 22, marginTop: 4, flexWrap: "wrap" }}>
        {fsa ? <>
          <div><div style={{ ...num, fontSize: 24, fontWeight: 800 }}>{fsa.fields.toLocaleString("en-US")}</div><div style={{ fontSize: 11.5, color: SOFT }}>fields</div></div>
          <div><div style={{ ...num, fontSize: 24, fontWeight: 800 }}>{acRound(fsa.acres)}</div><div style={{ fontSize: 11.5, color: SOFT }}>acres</div></div>
        </> : <>
          <div><div style={{ ...num, fontSize: 24, fontWeight: 800 }}>{season.machineFields.toLocaleString("en-US")}</div><div style={{ fontSize: 11.5, color: SOFT }}>fields worked</div></div>
          <div><div style={{ ...num, fontSize: 24, fontWeight: 800 }}>{acRound(plantedAc)}</div><div style={{ fontSize: 11.5, color: SOFT }}>acres planted</div></div>
        </>}
        {prog && <div><div style={{ ...num, fontSize: 24, fontWeight: 800 }}>{acRound(prog.ac)}</div><div style={{ fontSize: 11.5, color: SOFT }}>harvested</div></div>}
      </div>
      {prog && prog.pct != null && <>
        <Bar pct={prog.pct} />
        <div style={{ fontSize: 11.5, color: SOFT, marginTop: 5 }}>
          {prog.pct}% of {prog.basis === "reported" ? "acres" : "planted acres"} harvested so far
        </div>
      </>}
      {!fsa && <div style={{ fontSize: 11.5, color: SOFT, marginTop: 6 }}>Your reported acres for {year} aren't on file here yet.</div>}
      {loadingMachine && <div style={{ fontSize: 11.5, color: SOFT, marginTop: 6 }}>Loading your machine data…</div>}
    </div>

    {/* BY CROP */}
    {season.cropRows.length > 0 && (
      <div style={{ ...card, padding: "10px 14px 4px" }}>
        <div style={{ ...lbl, marginBottom: 4 }}>By crop</div>
        <div style={{ display: "grid", gridTemplateColumns: "14px minmax(0,1fr) 62px 112px", gap: 8, fontSize: 10.5, color: MUTED, padding: "2px 0 4px", borderBottom: "1px solid #e3eae0" }}>
          <span /><span>Crop</span><span style={{ textAlign: "right" }}>Acres</span><span style={{ textAlign: "right" }}>Avg yield</span>
        </div>
        {season.cropRows.map((r, i) => (
          <button key={r.key} onClick={() => setPage({ kind: "crop", key: r.key })}
            style={{ display: "grid", gridTemplateColumns: "14px minmax(0,1fr) 62px 112px", alignItems: "center", gap: 8, width: "100%", minHeight: 48,
                     padding: "9px 0", border: "none", borderBottom: i < season.cropRows.length - 1 ? "1px solid #eef2ec" : "none", background: "none",
                     fontFamily: "inherit", color: INK, textAlign: "left", cursor: "pointer" }}>
            <Swatch color={rowColor(r)} />
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 14, fontWeight: 700 }}>{r.label}</span>
              <span style={{ display: "block", fontSize: 11, fontWeight: 500, color: SOFT }}>
                {r.split && r.split.some((x) => x.key !== "not_yet" && x.key !== "pp")
                  ? splitShort(r.split)
                  : r.fsaFields != null ? plural(r.fsaFields, "field") : "harvested, from your machines"}
              </span>
            </span>
            <span style={{ ...num, textAlign: "right", fontSize: 14, fontWeight: 700 }}>{acRound(r.fsaAc != null ? r.fsaAc : r.harvAc)}</span>
            <span style={{ textAlign: "right" }}>
              {r.yields.length || (r.appraisal && r.appraisal.buAc != null) ? <>
                {r.yields.map((y) => (
                  <span key={y.key} style={{ display: "block", lineHeight: 1.25 }}>
                    {(r.yields.length > 1 || y.label !== r.label || r.appraisal) && <span style={{ display: "block", fontSize: 10, fontWeight: 600, color: MUTED }}>{y.label}</span>}
                    <span style={{ ...num, fontSize: 14, fontWeight: 800 }}>{fmtAvg(y.avg)}</span>{" "}
                    <span style={{ fontSize: 11, fontWeight: 600, color: SOFT }}>{avgUnitText(y.unit)}</span>
                  </span>
                ))}
                {r.appraisal && r.appraisal.buAc != null && (
                  <span style={{ display: "block", lineHeight: 1.25 }}>
                    <span style={{ display: "block", fontSize: 10, fontWeight: 600, color: MUTED }}>Appraised</span>
                    <span style={{ ...num, fontSize: 14, fontWeight: 800 }}>{fmtAvg(r.appraisal.buAc)}</span>{" "}
                    <span style={{ fontSize: 11, fontWeight: 600, color: SOFT }}>bu/ac</span>
                  </span>
                )}
              </> : (
                <span style={{ fontSize: 11.5, fontWeight: 700, color: MUTED }}>
                  {loadingMachine ? "…" : r.harvAc > 0 ? "no yield map" : r.plantAc > 0 ? "not harvested yet" : "—"}
                </span>
              )}
            </span>
          </button>
        ))}
      </div>
    )}

    {/* MAPS */}
    {machineLine ? (
      <div style={{ ...card, fontSize: 13, color: SOFT, lineHeight: 1.5 }}>{machineLine}</div>
    ) : (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
        {[["yield", "Yield maps", <YieldIcon key="i" />, season.mapCounts.yieldFields, "with a yield map"],
          ["seed_rate", "Planting maps", <PlantIcon key="i" />, season.mapCounts.plantFields, "planted"]].map(([m, t, icon, n, what]) => (
          <button key={m} onClick={() => openMaps(m)}
            style={{ height: 92, borderRadius: 16, border: "none", background: INK, color: "#eef5ea", fontFamily: "inherit", textAlign: "left",
                     padding: 12, display: "flex", flexDirection: "column", justifyContent: "space-between", cursor: "pointer" }}>
            {icon}
            <span>
              <span style={{ fontSize: 15, fontWeight: 800, display: "block" }}>{t}</span>
              <span style={{ fontSize: 11.5, color: "#a0bea0" }}>{loadingMachine ? "Loading…" : n > 0 ? `${plural(n, "field")} ${what}` : `None yet for ${year}`}</span>
            </span>
          </button>
        ))}
      </div>
    )}

    {/* TOTALS */}
    <button onClick={() => setPage({ kind: "totals" })}
      style={{ marginTop: 10, width: "100%", height: 52, borderRadius: 14, border: `1px solid ${BORDER}`, background: "#fff", color: INK, fontFamily: "inherit",
               fontSize: 14.5, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px", cursor: "pointer" }}>
      <span>Season totals by crop and type</span><span aria-hidden="true" style={{ color: SOFT, fontSize: 18 }}>›</span>
    </button>
  </>);
}

function CropPage({ row, season, data, year, farm, header, wrap, onBack, onPick, onField, color, h, loadingMachine, machineLine }) {
  const [sort, setSort] = useState("yield");
  const fields = useMemo(() => cropFields((data && data.machine && data.machine.byField) || [], year, row.machineCrops, data && data.calibration, h),
    [data, year, row, h]);
  const sorted = useMemo(() => {
    const f = [...fields];
    if (sort === "name") f.sort((a, b) => String(a.field).localeCompare(String(b.field), undefined, { numeric: true }));
    else if (sort === "acres") f.sort((a, b) => (b.ac || 0) - (a.ac || 0));
    else f.sort((a, b) => (b.yield == null ? -1 : b.yield) - (a.yield == null ? -1 : a.yield) || String(a.field).localeCompare(String(b.field), undefined, { numeric: true }));
    return f;
  }, [fields, sort]);
  // The corn row's appraisals, side by side with the machine yield on the same John Deere
  // field (each insured field goes to the one John Deere field that worked most of it), and
  // an appraised field no machine field covers on a row of its own.
  const cornFields = row.crop === "CORN" && row.cornFields ? row.cornFields : null;
  const apprByJd = useMemo(() => {
    const m = new Map();
    for (const f of cornFields || []) {
      if (!f.appraisal || !f.jdFieldId) continue;
      const e = m.get(f.jdFieldId) || { ac: 0, bu: 0, farmLevel: false };
      if (f.appraisal.farmLevel) e.farmLevel = true; else { e.ac += f.appraisal.ac || 0; e.bu += f.appraisal.bu || 0; }
      m.set(f.jdFieldId, e);
    }
    return m;
  }, [cornFields]);
  const apprOnly = useMemo(() => {
    const onMachine = new Set(fields.map((f) => f.jd_field_id).filter(Boolean));
    return (cornFields || []).filter((f) => f.appraisal && !(f.jdFieldId && onMachine.has(f.jdFieldId)))
      .map((f) => ({ key: `ap:${f.clu}`, field: farmField(f.fsn, f.field), ac: f.insuredAc, method: f.method, appr: f.appraisal, apprOnly: true,
                     // The field's own insured type on the acreage report (corn 026 = silage).
                     insuredSilage: String(f.type || "").trim() === "026" }));
  }, [cornFields, fields]);
  const nextSort = { yield: "name", name: "acres", acres: "yield" };
  const mixed = row.machineCrops.length > 1 && fields.some((f) => f.crop !== fields[0].crop);
  const acres = row.fsaAc != null ? row.fsaAc : row.plantAc > 0 ? row.plantAc : row.harvAc;
  const pct = acres > 0 ? Math.min(100, Math.round((100 * row.harvAc) / acres)) : null;
  return wrap(<>
    {header(`${row.label} · ${year}`, farm.name, onBack)}
    {season.cropRows.length > 1 && (
      <div style={{ display: "flex", gap: 8, overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none", padding: "2px 0 4px" }}>
        {season.cropRows.map((r) => {
          const on = r.key === row.key;
          return (
            <button key={r.key} onClick={() => onPick(r.key)}
              style={{ flexShrink: 0, height: 40, padding: "0 14px", borderRadius: 20, border: on ? "none" : `1px solid ${BORDER}`, background: on ? INK : "#fff",
                       color: on ? ACCENT : INK, fontFamily: "inherit", fontSize: 13, fontWeight: on ? 800 : 700, whiteSpace: "nowrap", cursor: "pointer" }}>
              {r.label} {acRound(r.fsaAc != null ? r.fsaAc : r.harvAc)}
            </button>
          );
        })}
      </div>
    )}
    <div style={card}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 8 }}>
        <div><div style={{ ...num, fontSize: 19, fontWeight: 800 }}>{(row.fsaFields != null ? row.fsaFields : row.machineFields).toLocaleString("en-US")}</div><div style={{ fontSize: 11, color: SOFT }}>fields</div></div>
        <div><div style={{ ...num, fontSize: 19, fontWeight: 800 }}>{acRound(acres)} ac</div><div style={{ fontSize: 11, color: SOFT }}>{row.fsaAc != null ? "reported" : row.plantAc > 0 ? "planted" : "harvested"}</div></div>
        <div>
          {row.yields.length ? row.yields.map((y) => (
            <div key={y.key}>
              <div style={{ ...num, fontSize: 19, fontWeight: 800 }}>{fmtAvg(y.avg)}</div>
              <div style={{ fontSize: 11, color: SOFT }}>{row.yields.length > 1 || y.label !== row.label ? `${y.label} · ` : ""}{avgUnitText(y.unit)} so far</div>
            </div>
          )) : !(row.appraisal && row.appraisal.buAc != null)
            ? <><div style={{ fontSize: 19, fontWeight: 800 }}>—</div><div style={{ fontSize: 11, color: SOFT }}>{loadingMachine ? "loading…" : "no yield map yet"}</div></> : null}
          {row.appraisal && row.appraisal.buAc != null && (
            <div>
              <div style={{ ...num, fontSize: 19, fontWeight: 800 }}>{fmtAvg(row.appraisal.buAc)}</div>
              <div style={{ fontSize: 11, color: SOFT }}>Appraised · bu/ac</div>
            </div>
          )}
        </div>
      </div>
      {row.split ? (
        <SplitBar split={row.split} total={acres} color={color} />
      ) : pct != null && <>
        <Bar pct={pct} color={color} />
        <div style={{ fontSize: 11.5, color: SOFT, marginTop: 5 }}>{acRound(row.harvAc)} of {acRound(acres)} acres harvested</div>
      </>}
      {row.yields.some((y) => y.dmAvg != null) && (
        <div style={{ fontSize: 11.5, color: SOFT, marginTop: 5 }}>
          {row.yields.filter((y) => y.dmAvg != null).map((y) => `${y.label}: ${fmtAvg(y.dmAvg)} dry matter ton/ac`).join(" · ")}
        </div>
      )}
      {row.types && (
        <div style={{ fontSize: 11.5, color: SOFT, marginTop: 5 }}>
          Insured as {row.types.map((t) => `${t.label} ${acRound(t.acres)} ac`).join(" · ")}
        </div>
      )}
    </div>

    {(row.appraisal || row.appraisalSilageInsured) && <AppraisalCard a={row.appraisal} sil={row.appraisalSilageInsured} />}

    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0 6px" }}>
      <span style={lbl}>Fields</span>
      {fields.length > 1 && (
        <button onClick={() => setSort(nextSort[sort])}
          style={{ height: 36, padding: "0 12px", borderRadius: 8, border: `1px solid ${BORDER}`, background: "#fff", color: INK, fontFamily: "inherit", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
          Sort: {sort}
        </button>
      )}
    </div>
    {machineLine ? (
      <div style={{ ...card, marginTop: 0, fontSize: 13, color: SOFT, lineHeight: 1.5 }}>{machineLine}</div>
    ) : loadingMachine ? (
      <div style={{ ...card, marginTop: 0, fontSize: 13, color: SOFT }}>Loading your fields…</div>
    ) : !fields.length && !apprOnly.length ? (
      <div style={{ ...card, marginTop: 0, fontSize: 13, color: SOFT, lineHeight: 1.5 }}>Your John Deere machines haven't recorded any {row.label.toLowerCase()} for {year} yet.</div>
    ) : null}
    {(() => {
      const list = [...(machineLine || loadingMachine ? [] : sorted), ...apprOnly];
      if (!list.length) return null;
      const shown = new Set();
      return <>
        <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 16, overflow: "hidden", marginTop: machineLine ? 10 : 0 }}>
          {list.map((f, i) => {
            const bits = f.apprOnly
              ? [f.ac != null ? `${fmtAc(f.ac)} ac insured${f.insuredSilage ? " as silage" : ""}` : null, METHOD_WORD[f.method] || null].filter(Boolean)
              : [f.ac != null ? `${fmtAc(f.ac)} ac` : null,
                 mixed ? (f.crop === "CORN SILAGE" ? "chopped" : "combined") : null,
                 f.plant_date ? `Planted ${mdText(f.plant_date)}` : null,
                 f.harv_date && f.state !== "no_acres" ? `Harvested ${mdText(f.harv_date)}` : (f.state === "not_harvested" ? "Harvest pending" : null)].filter(Boolean);
            // The appraisal on this John Deere field, shown once, beside its machine yield.
            let appr = f.apprOnly ? f.appr : null;
            if (!f.apprOnly && f.jd_field_id && apprByJd.has(f.jd_field_id) && !shown.has(f.jd_field_id)) {
              shown.add(f.jd_field_id);
              const e = apprByJd.get(f.jd_field_id);
              appr = e.ac > 0 ? { ac: e.ac, bu: e.bu, buAc: +(e.bu / e.ac).toFixed(1) } : e.farmLevel ? { farmLevel: true } : null;
            }
            const can = !f.apprOnly && !!f.jd_field_id;
            return (
              <button key={f.key} onClick={() => can && onField(f)} disabled={!can}
                style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", minHeight: 56, padding: "11px 14px", border: "none",
                         borderBottom: i < list.length - 1 ? "1px solid #eef2ec" : "none", background: "none", fontFamily: "inherit", color: INK,
                         textAlign: "left", cursor: can ? "pointer" : "default" }}>
                <span style={{ flexGrow: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 15, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.field}</span>
                  <span style={{ display: "block", fontSize: 11.5, color: SOFT, marginTop: 2 }}>{bits.join(" · ")}</span>
                </span>
                <span style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  {f.yield != null ? <>
                    <span style={{ ...num, fontSize: 16, fontWeight: 800 }}>{fmtAvg(f.yield)}</span>{" "}
                    <span style={{ fontSize: 11, color: SOFT }}>{avgUnitText(f.unit)}</span>
                    {f.dm != null && <span style={{ display: "block", fontSize: 10.5, color: SOFT }}>{fmtAvg(f.dm)} DM</span>}
                  </> : !f.apprOnly ? <span style={{ fontSize: 12, color: f.state === "no_map" ? "#8a6a1a" : MUTED, fontWeight: 700 }}>{STATE_TEXT[f.state]}</span> : null}
                  {appr && (appr.farmLevel
                    ? <span style={{ display: "block", fontSize: 10.5, color: SOFT }}>appraised with other fields</span>
                    : appr.buAc != null && (
                      f.apprOnly ? <>
                        <span style={{ ...num, fontSize: 16, fontWeight: 800 }}>{fmtAvg(appr.buAc)}</span>{" "}
                        <span style={{ fontSize: 11, color: SOFT }}>bu/ac appraised</span>
                        <span style={{ display: "block", fontSize: 10.5, color: SOFT }}>on {fmtAc(appr.ac)} ac</span>
                      </> : <span style={{ display: "block", fontSize: 11.5, color: INK, fontWeight: 700, marginTop: 2 }}>
                        {fmtAvg(appr.buAc)} <span style={{ fontWeight: 500, color: SOFT }}>bu/ac appraised</span>
                      </span>))}
                </span>
                {can && <span aria-hidden="true" style={{ color: "#8aa08a", fontSize: 18 }}>›</span>}
              </button>
            );
          })}
        </div>
        {fields.length > 0 && !machineLine && <div style={{ textAlign: "center", fontSize: 12, color: SOFT, marginTop: 8 }}>Tap a field to see its yield and planting maps</div>}
      </>;
    })()}
    {row.unplacedAppraisals && row.unplacedAppraisals.length > 0 && (
      <div style={{ ...card }}>
        <div style={lbl}>Appraisals not tied to one field</div>
        <div style={{ fontSize: 11.5, color: SOFT, margin: "4px 0 6px", lineHeight: 1.45 }}>
          These are in your appraisal totals. The worksheet line doesn't match a single field on your acreage report.
        </div>
        {row.unplacedAppraisals.map((u, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13, padding: "6px 0", borderTop: i ? "1px solid #eef2ec" : "none" }}>
            <span style={{ minWidth: 0 }}>{farmField(u.fsn, u.fieldNo || u.fieldName)}{u.coversSeveral ? " · covers several fields" : ""}</span>
            <span style={{ ...num, whiteSpace: "nowrap", fontWeight: 700 }}>
              {u.ac != null ? `${fmtAc(u.ac)} ac` : "—"}{u.buAc != null ? ` · ${fmtAvg(u.buAc)} bu/ac` : ""}
            </span>
          </div>
        ))}
      </div>
    )}
  </>);
}

const METHOD_WORD = { combined: "combined", chopped: "chopped", both: "chopped and combined", appraised: "appraised, no machine data", unclear: "harvested",
                      map_pending: "harvested, yield map not built yet", not_yet: "no harvest on file yet", pp: "prevent plant" };

// How the corn acres were harvested: one bar, each way of harvest its own share, every share
// named with its acres underneath so nothing is read off a colour.
function SplitBar({ split, total, color }) {
  const sum = split.reduce((s, x) => s + x.ac, 0) || 1;
  const col = (k) => SPLIT_COLOR[k] || (k === "chopped" ? "#8a6d1f" : color || "#54a24a");
  return <>
    <div style={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", marginTop: 10, background: "#e3eae0" }}>
      {split.map((x) => <div key={x.key} style={{ width: `${(100 * x.ac) / sum}%`, background: col(x.key) }} />)}
    </div>
    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", marginTop: 6 }}>
      {split.map((x) => (
        <span key={x.key} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: SOFT }}>
          <Swatch color={col(x.key)} size={9} /><span><b style={{ color: INK, ...num }}>{acRound(x.ac)} ac</b> {x.label.toLowerCase()}</span>
        </span>
      ))}
    </div>
  </>;
}

// The appraisals on file for this crop, in grain bushels, beside (never in place of) the
// machine yield. No claim figure, no guarantee arithmetic, no dollars.
// `sil` is what was appraised on acres INSURED AS SILAGE (Kevin, 2026-11-02: "report it as is
// to match how it's insured"): said on its own line, never added to the grain figures.
function AppraisalCard({ a, sil }) {
  const silLine = sil ? plural(sil.lines, "appraisal line") + " on " + acRound(sil.ac) + " ac insured as silage: "
    + fmtProd(sil.bu) + " bu appraised, not in the grain figures." : null;
  if (!a) {
    return (
      <div style={card}>
        <span style={lbl}>Appraisals</span>
        <div style={{ fontSize: 11.5, color: SOFT, marginTop: 6, lineHeight: 1.45 }}>{silLine}</div>
      </div>
    );
  }
  const dates = a.from && a.to ? (a.from === a.to ? mdText(a.from) : `${mdText(a.from)} to ${mdText(a.to)}`) : null;
  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <span style={lbl}>Appraisals</span>{dates && <span style={{ fontSize: 11, color: MUTED }}>{dates}</span>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 8, marginTop: 6 }}>
        <div><div style={{ ...num, fontSize: 19, fontWeight: 800 }}>{acRound(a.ac)} ac</div><div style={{ fontSize: 11, color: SOFT }}>appraised</div></div>
        <div><div style={{ ...num, fontSize: 19, fontWeight: 800 }}>{fmtAvg(a.buAc)}</div><div style={{ fontSize: 11, color: SOFT }}>bu/ac appraised</div></div>
        <div><div style={{ ...num, fontSize: 19, fontWeight: 800 }}>{fmtProd(a.bu)}</div><div style={{ fontSize: 11, color: SOFT }}>bu appraised</div></div>
      </div>
      <div style={{ fontSize: 11.5, color: SOFT, marginTop: 6, lineHeight: 1.45 }}>
        {plural(a.lines, "appraisal line")}{a.fields ? ` on ${plural(a.fields, "field")}` : ""}.
        {a.onChoppedAc > 0.05 ? ` ${acRound(a.onChoppedAc)} appraised acres were later chopped.` : ""}
        {a.onCombinedAc > 0.05 ? ` ${acRound(a.onCombinedAc)} appraised acres were later combined.` : ""}
        {" "}Appraised in grain bushels; machine yields stay in their own units.
        {silLine ? ` ${silLine}` : ""}
      </div>
    </div>
  );
}

function TotalsPage({ season, data, year, farm, header, wrap, onBack, colorOf, h, loadingMachine, machineLine, gridsLoading }) {
  const [busy, setBusy] = useState(false);
  const [pdfErr, setPdfErr] = useState(null);
  const tableRow = { display: "grid", gridTemplateColumns: "12px minmax(0,1fr) 78px 112px", gap: 8, alignItems: "center", padding: "8px 0", borderBottom: "1px solid #eef2ec" };
  const download = async () => {
    setBusy(true); setPdfErr(null);
    try {
      const mod = await import("pdf-lib");   // loaded on demand: its own chunk, not the main bundle
      const PDFLib = mod && mod.PDFDocument ? mod : mod.default;
      const byField = (data && data.machine && data.machine.byField) || [];
      const bytes = await buildSeasonPdf({
        farmName: farm.name, year, generatedAt: new Date(), harvest: season.harvest, unmapped: season.harvestUnmapped,
        corn: season.cropRows.find((r) => r.key === "fsa:CORN") || null,
        fieldsByLine: fieldsByLine(byField, year, season.harvest, data && data.calibration, h),
      }, PDFLib);
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${String(farm.name || "Farm").replace(/[^A-Za-z0-9 _-]+/g, "").trim() || "Farm"} ${year} yield summary.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setPdfErr("The PDF didn't build. Try again in a moment.");
    } finally { setBusy(false); }
  };
  // The one Corn row's harvest split and its appraisal. Shown with or without John Deere: a
  // farm with appraisals only (Mulligan) still has its appraisals to see.
  const cornRow = season.cropRows.find((r) => r.key === "fsa:CORN");
  const cornCards = cornRow ? <>
    {cornRow.split && (machineLine ? cornRow.splitBasis === "fields" : true) && (
      <div style={{ ...card }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={lbl}>Corn · how it was harvested</span><span style={{ fontSize: 11, color: MUTED }}>{acRound(cornRow.fsaAc)} ac reported</span>
        </div>
        <SplitBar split={cornRow.split} total={cornRow.fsaAc} color={colorOf("CORN")} />
      </div>
    )}
    {(cornRow.appraisal || cornRow.appraisalSilageInsured) && <AppraisalCard a={cornRow.appraisal} sil={cornRow.appraisalSilageInsured} />}
  </> : null;
  return wrap(<>
    {header(`${year} season totals`, "Each crop on its own line. Crops are never averaged together.", onBack)}
    {machineLine && <div style={{ ...card, fontSize: 13, color: SOFT, lineHeight: 1.5 }}>{machineLine}</div>}
    {machineLine && cornCards}
    {loadingMachine && <div style={{ ...card, fontSize: 13, color: SOFT }}>Loading your machine data…</div>}
    {!machineLine && !loadingMachine && <>
      <div style={{ ...card, padding: "10px 14px 2px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={lbl}>Planting</span><span style={{ fontSize: 11, color: MUTED }}>from planter maps</span>
        </div>
        {!season.planting.length ? (
          <div style={{ fontSize: 13, color: SOFT, padding: "8px 0 10px" }}>No planting recorded for {year}.</div>
        ) : season.planting.map((l) => (
          <div key={l.key} style={tableRow}>
            <Swatch color={colorOf(l.crop)} size={11} />
            <span style={{ fontSize: 13.5, fontWeight: 700, minWidth: 0 }}>{l.label}</span>
            <span style={{ ...num, textAlign: "right", fontSize: 13.5, fontWeight: 700 }}>{fmtAc(l.ac)} ac</span>
            <span style={{ textAlign: "right" }}>
              {l.rates.length ? l.rates.map((r) => (
                <span key={r.unit} style={{ display: "block", lineHeight: 1.25 }}>
                  <span style={{ ...num, fontSize: 13.5, fontWeight: 800 }}>{fmtRate(r.avg)}</span>
                  <span style={{ display: "block", fontSize: 10.5, color: SOFT }}>{r.unit} avg</span>
                </span>
              )) : <span style={{ fontSize: 11, color: MUTED }}>{gridsLoading ? "…" : "no rate on the planter map"}</span>}
            </span>
          </div>
        ))}
      </div>

      <div style={{ ...card, padding: "10px 14px 2px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={lbl}>Harvest</span><span style={{ fontSize: 11, color: MUTED }}>from yield maps</span>
        </div>
        {!season.harvest.length ? (
          <div style={{ fontSize: 13, color: SOFT, padding: "8px 0 10px" }}>No harvest with a yield map for {year} yet.</div>
        ) : season.harvest.map((l) => {
          const pu = prodUnitText(l.unit);
          return (
            <div key={l.key} style={tableRow}>
              <Swatch color={colorOf(l.crop)} size={11} />
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 13.5, fontWeight: 700 }}>{l.label}</span>
                {(l.noMapAc > 0 || l.calibrated || l.dmAvg != null) && (
                  <span style={{ display: "block", fontSize: 10.5, color: SOFT, lineHeight: 1.35 }}>
                    {[l.dmAvg != null ? `${fmtAvg(l.dmAvg)} dry matter ton/ac` : null,
                      l.noMapAc > 0 ? `+${fmtAc(l.noMapAc)} ac with no yield map` : null,
                      l.calibrated ? "adjusted to your entered total" : null].filter(Boolean).join(" · ")}
                  </span>
                )}
              </span>
              <span style={{ ...num, textAlign: "right", fontSize: 13.5, fontWeight: 700 }}>{fmtAc(l.ac)} ac</span>
              <span style={{ textAlign: "right", lineHeight: 1.25 }}>
                <span style={{ ...num, fontSize: 13.5, fontWeight: 800 }}>{fmtAvg(l.avg)} {avgUnitText(l.unit)}</span>
                <span style={{ ...num, display: "block", fontSize: 10.5, color: SOFT }}>{fmtProd(l.prod)} {pu || "(unit not recorded)"}</span>
              </span>
            </div>
          );
        })}
        {season.harvestUnmapped.map((u) => (
          <div key={u.crop} style={{ fontSize: 12, color: SOFT, padding: "8px 0" }}>{u.label}: {fmtAc(u.noMapAc)} ac harvested, no yield map yet</div>
        ))}
      </div>

      {cornCards}
      {(season.unknownCrop && season.unknownCrop.harvAc > 0) && (
        <div style={{ ...card, fontSize: 12.5, color: SOFT, lineHeight: 1.45 }}>
          {fmtAc(season.unknownCrop.harvAc)} ac were harvested with no crop set in John Deere Operations Center, so they aren't on any line here. Set the crop on those jobs in Operations Center and they'll show up.
        </div>
      )}
      {season.forageNotShown && season.forageNotShown.length > 0 && (
        <div style={{ ...card, fontSize: 12.5, color: SOFT, lineHeight: 1.45 }}>
          {season.forageNotShown.map((c) => h.cropName(c)).join(", ")} isn't on your acreage report, so it isn't totaled here. Its machine acres add up every cutting, which would overstate the acres.
        </div>
      )}

      {season.stillToHarvest.length > 0 && (
        <div style={{ marginTop: 10, background: "#fff8e6", border: "1px solid #f0d58a", borderRadius: 14, padding: "10px 12px", fontSize: 12.5, color: "#6a4f10", lineHeight: 1.45 }}>
          <b>No harvest on file yet:</b> {season.stillToHarvest.map((s) => `${s.label} ${acRound(s.ac)} ac`).join(", ")}. Totals update as your machines sync.
        </div>
      )}

      <button onClick={download} disabled={busy}
        style={{ marginTop: 10, width: "100%", height: 48, borderRadius: 12, border: `1px solid ${BORDER}`, background: "#fff", color: INK, fontFamily: "inherit",
                 fontSize: 14, fontWeight: 800, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
        {busy ? "Building PDF…" : "Download yield summary PDF"}
      </button>
      {pdfErr && <div style={{ fontSize: 12.5, color: "#a33", marginTop: 6 }}>{pdfErr}</div>}
    </>}
  </>);
}
